import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, ArrowDown, Lock, Users, Target, Clock, Briefcase, FileText,
  Compass, Package, Megaphone, Rocket, Quote, CheckCircle2, XCircle, Mail, PhoneCall,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { NewsletterForm } from "../components/NewsletterForm";
import { StatCounter } from "../components/StatCounter";
import { api } from "../lib/api";

const signs = [
  { icon: Lock, title: "Ton TJM plafonne depuis 2 ans", text: "Tu négocies 20€ de plus par mission pendant que ta valeur réelle, elle, a doublé." },
  { icon: Users, title: "Un client = plus de 80% de ton chiffre d'affaires", text: "Une seule ESN, un seul donneur d'ordre. S'il coupe la mission, ton activité s'arrête net." },
  { icon: Clock, title: "Tu vends des jours, pas des résultats", text: "Plus tu es rapide et bon, moins tu gagnes. Le modèle régie pénalise l'excellence." },
  { icon: Briefcase, title: "L'intercontrat te terrifie", text: "Sans mission, zéro revenu. Tu subis ton pipeline au lieu de le piloter." },
  { icon: FileText, title: "Tu n'as pas d'offre, tu as un CV", text: "On t'achète un profil, pas une solution. Impossible de facturer la valeur dans ces conditions." },
  { icon: Target, title: "Ta prospection dépend des autres", text: "Apporteurs, plateformes, ESN : ton acquisition ne t'appartient pas. Tu es locataire de ton business." },
];

const offers = [
  { num: "01", title: "Un positionnement de valeur", text: "On transforme ton expertise en offre packagée qui vend un résultat, pas des heures. Fini le CV : place à une proposition que tes prospects comprennent en 30 secondes." },
  { num: "02", title: "Un système d'acquisition propre", text: "Contenu, preuve, pipeline : tu construis un canal qui t'appartient et qui amène des prospects qualifiés, sans dépendre des ESN ni des plateformes." },
  { num: "03", title: "Une bascule sécurisée", text: "Tu signes tes premiers forfaits pendant que la régie finance la transition. Pas de saut dans le vide : un plan de bascule palier par palier." },
];

const methodSteps = [
  { icon: Compass, title: "Audit de trajectoire", text: "On cartographie ton positionnement actuel, tes actifs et tes angles morts." },
  { icon: Package, title: "Construction de l'offre", text: "On package ton expertise en forfait à forte valeur perçue, prêt à vendre." },
  { icon: Megaphone, title: "Système d'acquisition", text: "On installe ton moteur de visibilité : contenu, preuve sociale, pipeline." },
  { icon: Rocket, title: "Exécution accompagnée", text: "Tu exécutes avec un accès direct à Simon. On itère jusqu'au premier forfait signé." },
];

const Simulator = () => {
  const [tjm, setTjm] = useState(550);
  const [days, setDays] = useState(16);
  const regieCA = tjm * days;
  const forfaitCA = Math.round(regieCA * 1.6 / 100) * 100;
  const forfaitDays = Math.round(days * 0.6);
  const fmt = (n) => n.toLocaleString("fr-FR");

  return (
    <div data-testid="tjm-simulator" className="glass-card p-6 sm:p-10 grain relative">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-8">
          <div>
            <div className="flex justify-between mb-3">
              <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Ton TJM actuel</label>
              <span className="font-heading font-bold text-primary">{fmt(tjm)} €/j</span>
            </div>
            <input type="range" min="300" max="1200" step="10" value={tjm}
              onChange={(e) => setTjm(+e.target.value)}
              data-testid="tjm-simulator-input"
              className="w-full accent-[#00E599]" />
          </div>
          <div>
            <div className="flex justify-between mb-3">
              <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Jours facturés / mois</label>
              <span className="font-heading font-bold text-primary">{days} j</span>
            </div>
            <input type="range" min="8" max="21" value={days}
              onChange={(e) => setDays(+e.target.value)}
              data-testid="days-simulator-input"
              className="w-full accent-[#00E599]" />
          </div>
          <p className="text-xs text-slate-500 font-mono">Projection indicative basée sur les bascules observées dans l'accompagnement.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-2xl border border-[#262B3D] bg-[#0D1017] p-5">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Aujourd'hui · régie</p>
            <p className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-300" data-testid="sim-regie-ca">{fmt(regieCA)} €</p>
            <p className="text-xs text-slate-500 mt-1">{days} jours vendus / mois</p>
          </div>
          <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5 relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-primary/20 blur-2xl" />
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Après · forfait</p>
            <p className="font-heading text-2xl sm:text-3xl font-extrabold text-primary" data-testid="sim-forfait-ca">{fmt(forfaitCA)} €</p>
            <p className="text-xs text-slate-400 mt-1">≈ {forfaitDays} jours de travail / mois</p>
          </div>
          <div className="col-span-2 rounded-2xl border border-[#262B3D] bg-[#0D1017] p-5 flex items-center justify-between">
            <p className="text-sm text-slate-300">Temps libéré chaque mois</p>
            <p className="font-heading text-xl font-extrabold text-white" data-testid="sim-days-gained">+{days - forfaitDays} jours</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  const [content, setContent] = useState(null);
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#0A0C10]">
      <Navbar />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden grain pt-16">
        <motion.div style={{ y: glowY }} className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-primary/[0.07] blur-[120px]" />
          <div className="absolute bottom-[-30%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/[0.05] blur-[100px]" />
        </motion.div>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#00E599 1px, transparent 1px), linear-gradient(90deg, #00E599 1px, transparent 1px)", backgroundSize: "72px 72px" }} />

        <motion.div style={{ opacity: heroOpacity }} className="container-site relative py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-primary/40 bg-primary/5 rounded-full px-4 py-2 mb-8"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Régie → Forfait</span>
          </motion.div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] max-w-5xl">
            <MaskedLine delay={0.15}>Arrête de vendre</MaskedLine>
            <MaskedLine delay={0.3}>ton temps.</MaskedLine>
            <MaskedLine delay={0.45} className="text-primary text-glow">Vends ton expertise.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            On construit le système qui fait basculer ton activité de freelance tech de la régie au forfait :
            offre packagée, acquisition qui t'appartient, revenus découplés de tes heures.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => navigate("/offre#postuler")}
              data-testid="hero-cta-apply"
              className="group inline-flex items-center justify-center gap-2 bg-primary text-[#0A0C10] font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-[#00C885] hover:shadow-[0_0_50px_rgba(0,229,153,0.45)] hover:-translate-y-0.5"
            >
              Booker mon Check Up
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <Link
              to="/newsletter"
              data-testid="hero-cta-newsletter"
              className="inline-flex items-center justify-center gap-2 border border-[#262B3D] text-slate-200 font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:border-primary hover:text-primary hover:-translate-y-0.5"
            >
              Rejoindre la newsletter
            </Link>
          </motion.div>

          {content && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.15 }}
              className="mt-14 flex flex-wrap gap-3"
              data-testid="hero-stats-pills"
            >
              {content.stats.slice(0, 3).map((s) => (
                <div key={s.id} className="flex items-center gap-2 border border-[#262B3D] bg-[#12151F]/60 rounded-full px-4 py-2">
                  <span className="font-heading font-bold text-primary text-sm">
                    <StatCounter value={s.value} suffix={s.suffix} decimals={s.decimals} testid={`hero-stat-${s.id}`} />
                  </span>
                  <span className="text-xs text-slate-400">{s.label}</span>
                </div>
              ))}
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500"
        >
          <ArrowDown className="w-5 h-5 animate-bounce" />
        </motion.div>
      </section>

      {content && <Marquee items={content.marquee} />}

      {/* 6 SIGNES */}
      <section className="py-16 sm:py-24 lg:py-32 relative">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow mb-4">Chapitre 01 — Le constat</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight max-w-3xl leading-[1.1]">
              6 signes que tu es prêt à <span className="text-primary">basculer</span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl text-base">Si tu te reconnais dans 3 de ces signaux, ce n'est plus une question de talent. C'est une question de modèle.</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {signs.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`sign-card-${i}`}
                  className="group glass-card p-7 h-full transition-all duration-500 hover:border-primary/50 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,229,153,0.08)]"
                >
                  <s.icon className="w-6 h-6 text-primary mb-5 transition-transform duration-500 group-hover:scale-110" />
                  <h3 className="font-heading text-lg font-bold mb-2 leading-snug">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OFFRE 01/02/03 */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#0D1017] border-y border-[#262B3D] relative grain">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow mb-4">Chapitre 02 — Ce que tu construis</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight max-w-3xl leading-[1.1]">
              La bascule = positionnement <span className="text-primary">×</span> système
            </h2>
          </Reveal>
          <div className="mt-14 space-y-5">
            {offers.map((o, i) => (
              <Reveal key={o.num} delay={i * 0.1}>
                <div
                  data-testid={`offer-block-${o.num}`}
                  className="group grid md:grid-cols-[120px_1fr] gap-6 glass-card p-8 sm:p-10 transition-all duration-500 hover:border-primary/50"
                >
                  <span className="font-heading text-5xl sm:text-6xl font-extrabold text-[#262B3D] group-hover:text-primary/40 transition-colors duration-500 select-none">{o.num}</span>
                  <div>
                    <h3 className="font-heading text-xl sm:text-2xl font-bold mb-3">{o.title}</h3>
                    <p className="text-slate-400 leading-relaxed max-w-2xl text-base">{o.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MÉTHODE */}
      <section className="py-16 sm:py-24 lg:py-32">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow mb-4">Chapitre 03 — Le déroulé</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight max-w-3xl leading-[1.1]">
              Résultats = clarté <span className="text-primary">×</span> action
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl text-base">Pas de théorie. Un déroulé en 4 temps, exécuté avec toi, jusqu'au premier forfait signé.</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {methodSteps.map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`method-step-${i}`} className="relative glass-card p-7 h-full overflow-hidden group">
                  <span className="absolute top-4 right-5 font-mono text-xs text-slate-600">0{i + 1}</span>
                  <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110">
                    <m.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-base font-bold mb-2">{m.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      {content && (
        <section className="py-16 sm:py-24 bg-[#0D1017] border-y border-[#262B3D]">
          <div className="container-site">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-testid="stats-grid">
              {content.stats.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.08} className="text-center">
                  <p className="font-heading text-4xl sm:text-5xl font-extrabold text-primary text-glow">
                    <StatCounter value={s.value} suffix={s.suffix} decimals={s.decimals} testid={`stat-${s.id}`} />
                  </p>
                  <p className="mt-3 text-sm text-slate-400 max-w-[200px] mx-auto">{s.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SIMULATEUR */}
      <section className="py-16 sm:py-24 lg:py-32">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow mb-4">Chapitre 04 — Le calcul</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight max-w-3xl leading-[1.1]">
              Combien te coûte la régie, <span className="text-primary">chaque mois ?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <Simulator />
          </Reveal>
        </div>
      </section>

      {/* DEUX CHEMINS */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#0D1017] border-y border-[#262B3D] grain relative">
        <div className="container-site">
          <Reveal className="text-center">
            <p className="eyebrow mb-4">Chapitre 05 — Le choix</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">
              Deux chemins s'offrent à toi
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Reveal>
              <div data-testid="path-newsletter" className="glass-card p-8 sm:p-10 h-full flex flex-col transition-all duration-500 hover:border-primary/50 hover:-translate-y-1">
                <Mail className="w-8 h-8 text-primary mb-6" />
                <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">Gratuit · 5 min / semaine</p>
                <h3 className="font-heading text-2xl font-bold mb-3">Rejoins la newsletter</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-8 flex-1">
                  Chaque semaine : tips actionnables, plans d'action et veille business
                  pour construire ta bascule à ton rythme.
                </p>
                <NewsletterForm testidPrefix="home-newsletter" compact />
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="path-accompagnement" className="relative glass-card p-8 sm:p-10 h-full flex flex-col border-primary/40 bg-primary/[0.03] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_80px_rgba(0,229,153,0.12)]">
                <span className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-widest bg-primary text-[#0A0C10] font-semibold rounded-full px-3 py-1">Sélection</span>
                <PhoneCall className="w-8 h-8 text-primary mb-6" />
                <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">Accompagnement · sur candidature</p>
                <h3 className="font-heading text-2xl font-bold mb-3">Booke ton Check Up</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-8 flex-1">
                  Le programme qui construit ton système de bascule avec toi : offre, acquisition, exécution.
                  Réservé aux freelances dont l'activité est déjà lancée.
                </p>
                <button
                  onClick={() => navigate("/offre#postuler")}
                  data-testid="path-cta-apply"
                  className="group inline-flex items-center justify-center gap-2 bg-primary text-[#0A0C10] font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:bg-[#00C885] hover:shadow-[0_0_30px_rgba(0,229,153,0.35)]"
                >
                  Booker mon Check Up
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      {content && (
        <section className="py-16 sm:py-24 lg:py-32">
          <div className="container-site">
            <Reveal>
              <p className="eyebrow mb-4">Chapitre 06 — La preuve</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight max-w-3xl leading-[1.1]">
                Ils ont fait la <span className="text-primary">bascule</span>
              </h2>
            </Reveal>
            <div className="mt-14 grid md:grid-cols-3 gap-5">
              {content.testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.1}>
                  <figure data-testid={`testimonial-${i}`} className="glass-card p-8 h-full flex flex-col transition-all duration-500 hover:border-primary/50 hover:-translate-y-1">
                    <Quote className="w-6 h-6 text-primary mb-5" />
                    <figcaption className="font-heading text-lg font-bold text-primary leading-snug mb-4" data-testid={`testimonial-result-${i}`}>{t.result}</figcaption>
                    <blockquote className="text-sm text-slate-400 leading-relaxed flex-1">« {t.quote} »</blockquote>
                    <div className="mt-6 pt-5 border-t border-[#262B3D]">
                      <p className="font-semibold text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{t.role}</p>
                    </div>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {content && (
        <section className="py-16 sm:py-24 lg:py-32 bg-[#0D1017] border-y border-[#262B3D]">
          <div className="container-site grid lg:grid-cols-[1fr_1.4fr] gap-12">
            <Reveal>
              <p className="eyebrow mb-4">Chapitre 07 — Les objections</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-[1.1]">
                Pourquoi ce n'est pas du coaching. Ni une formation de plus.
              </h2>
              <p className="mt-4 text-slate-400 text-base">Les questions qu'on nous pose avant chaque bascule, traitées frontalement.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                {content.faqs.map((f, i) => (
                  <AccordionItem key={f.id} value={f.id} data-testid={`faq-accordion-item-${i}`} className="glass-card px-6 border-[#262B3D]">
                    <AccordionTrigger className="text-left font-heading font-semibold text-sm sm:text-base hover:text-primary hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="py-20 sm:py-32 relative overflow-hidden grain">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-[-40%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-primary/[0.08] blur-[120px]" />
        </div>
        <div className="container-site relative text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl mx-auto">
              Dans 12 mois, tu vendras encore des journées.<br />
              <span className="text-primary text-glow">Ou tu vendras un système.</span>
            </h2>
            <p className="mt-6 text-slate-400 max-w-xl mx-auto text-base">
              La seule différence entre les deux trajectoires : la décision que tu prends aujourd'hui.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => navigate("/offre#postuler")}
                data-testid="final-cta-apply"
                className="group inline-flex items-center justify-center gap-2 bg-primary text-[#0A0C10] font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-[#00C885] hover:shadow-[0_0_50px_rgba(0,229,153,0.45)] hover:-translate-y-0.5"
              >
                Booker mon Check Up
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <Link
                to="/newsletter"
                data-testid="final-cta-newsletter"
                className="inline-flex items-center justify-center gap-2 border border-[#262B3D] text-slate-200 font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:border-primary hover:text-primary"
              >
                D'abord, la newsletter
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs text-slate-500 font-mono">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Réponse sous 48h</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Sans engagement</span>
              <span className="inline-flex items-center gap-2"><XCircle className="w-3.5 h-3.5 text-slate-600" /> Pas de formation vidéo interminable</span>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
